import React, { useState } from 'react';
import { Radio, Search, MapPin, Mountain, TreePine, Navigation, Loader2, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { maidenheadToLatLng, isMaidenheadLocator, latLngToMaidenhead } from '../utils/maidenhead';
import { toast } from 'sonner';

export const ControlPanel = ({ 
  radius, 
  setRadius, 
  center, 
  setCenter, 
  onSearch, 
  isCalculating,
  onCenterChange,
  cachedSearches = [],
  onResetCache,
  showSummits,
  setShowSummits,
  showComforts,
  setShowComforts,
  summitCount = 0,
  comfortCount = 0
}) => {
  const [locatorInput, setLocatorInput] = useState('');

  // Current position as Maidenhead
  const currentLocator = latLngToMaidenhead(center[0], center[1]);

  const handleLocatorSubmit = () => {
    if (!locatorInput.trim()) {
      toast.error('Entrez un locator Maidenhead (ex: JN36bk)');
      return;
    }

    if (!isMaidenheadLocator(locatorInput)) {
      toast.error('Format invalide. Utilisez le format Maidenhead (ex: JN36bk)');
      return;
    }

    const coords = maidenheadToLatLng(locatorInput);
    if (coords) {
      setCenter([coords.lat, coords.lng]);
      onCenterChange && onCenterChange([coords.lat, coords.lng]);
      toast.success(`Centre défini: ${locatorInput.toUpperCase()}`);
      setLocatorInput('');
    } else {
      toast.error('Impossible de convertir ce locator');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLocatorSubmit();
    }
  };

  const handleResetCache = () => {
    if (onResetCache) {
      onResetCache();
      toast.success('Cache effacé !');
    }
  };

  return (
    <div 
      className="bg-zinc-950/95 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl w-80 max-h-[85vh] overflow-y-auto" 
      data-testid="control-panel"
    >
      {/* Header */}
      <div className="border-b border-white/10 px-5 py-4 sticky top-0 bg-zinc-950/98 backdrop-blur-md z-10">
        <div className="flex items-center gap-3">
          <Radio className="w-6 h-6 text-emerald-500" />
          <h1 className="font-inter text-xl font-bold text-white">HamBestSpots</h1>
        </div>
        <p className="text-zinc-500 text-xs mt-1">Trouvez votre spot radio idéal</p>
      </div>

      {/* Locator Input */}
      <div className="px-5 py-4 border-b border-white/10">
        <label className="font-barlow text-zinc-500 uppercase text-xs tracking-widest mb-2 block">
          <Navigation className="w-3 h-3 inline mr-1" />
          Locator Maidenhead
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={locatorInput}
            onChange={(e) => setLocatorInput(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            placeholder="JN36bk"
            maxLength={6}
            className="flex-1 h-10 px-3 bg-zinc-900/80 border border-zinc-700 rounded-md text-white text-sm font-mono uppercase placeholder:text-zinc-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 transition-all"
            data-testid="locator-input"
          />
          <button
            onClick={handleLocatorSubmit}
            className="h-10 px-4 bg-emerald-600/20 text-emerald-400 border border-emerald-500/50 rounded-md hover:bg-emerald-500 hover:text-black transition-all"
            data-testid="locator-submit"
          >
            OK
          </button>
        </div>
        <div className="text-xs text-zinc-600 mt-2">
          Format: 2 lettres + 2 chiffres + 2 lettres (ex: JN36bk)
        </div>
      </div>

      {/* Current Position */}
      <div className="px-5 py-3 border-b border-white/10 bg-zinc-900/30">
        <div className="flex items-center justify-between mb-1">
          <span className="text-zinc-500 text-xs uppercase tracking-wider">Position actuelle</span>
          <span className="font-mono text-sm text-cyan-400 font-bold" data-testid="current-locator">
            {currentLocator}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-zinc-600 text-xs">Coordonnées</span>
          <span className="font-mono text-xs text-zinc-400" data-testid="current-position">
            {center[0].toFixed(4)}°N, {center[1].toFixed(4)}°E
          </span>
        </div>
        <div className="text-xs text-emerald-400/70 mt-2 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span>Cliquez sur la carte pour changer de position</span>
        </div>
      </div>

      {/* Radius Selector */}
      <div className="px-5 py-4 border-b border-white/10">
        <label className="font-barlow text-zinc-500 uppercase text-xs tracking-widest mb-3 block">
          Rayon de Recherche
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="10"
            max="50"
            step="5"
            value={radius}
            onChange={(e) => setRadius(parseInt(e.target.value))}
            className="flex-1 h-2 bg-zinc-800 rounded-full appearance-none cursor-pointer accent-emerald-500"
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${((radius - 10) / 40) * 100}%, #27272a ${((radius - 10) / 40) * 100}%, #27272a 100%)`
            }}
            data-testid="radius-slider"
          />
          <span className="font-mono text-cyan-400 font-bold text-lg w-16 text-right" data-testid="radius-value">
            {radius} km
          </span>
        </div>
        <div className="flex justify-between text-xs text-zinc-600 mt-2">
          <span>10 km</span>
          <span>50 km</span>
        </div>
      </div>

      {/* Spot Types - Clickable to toggle visibility */}
      <div className="px-5 py-4 border-b border-white/10">
        <label className="font-barlow text-zinc-500 uppercase text-xs tracking-widest mb-3 block">
          Types de Spots (cliquer pour masquer/afficher)
        </label>
        <div className="space-y-3">
          {/* Points Hauts - Toggle */}
          <button
            onClick={() => setShowSummits(!showSummits)}
            className={`w-full flex items-center justify-between gap-3 p-2 rounded-md transition-all ${
              showSummits 
                ? 'bg-amber-500/10 border border-amber-500/30' 
                : 'bg-zinc-800/50 border border-zinc-700 opacity-60'
            }`}
            data-testid="toggle-summits"
          >
            <div className="flex items-center gap-3">
              <Mountain className={`w-5 h-5 ${showSummits ? 'text-amber-500' : 'text-zinc-500'}`} />
              <div className="text-left">
                <div className={`text-sm font-medium ${showSummits ? 'text-amber-400' : 'text-zinc-500'}`}>
                  Points Hauts
                  <span className="ml-2 text-xs opacity-70">({summitCount})</span>
                </div>
                <div className="text-xs text-zinc-500">Pics, collines + LOS</div>
              </div>
            </div>
            {showSummits ? (
              <Eye className="w-4 h-4 text-amber-400" />
            ) : (
              <EyeOff className="w-4 h-4 text-zinc-500" />
            )}
          </button>

          {/* Conforts - Toggle */}
          <button
            onClick={() => setShowComforts(!showComforts)}
            className={`w-full flex items-center justify-between gap-3 p-2 rounded-md transition-all ${
              showComforts 
                ? 'bg-emerald-500/10 border border-emerald-500/30' 
                : 'bg-zinc-800/50 border border-zinc-700 opacity-60'
            }`}
            data-testid="toggle-comforts"
          >
            <div className="flex items-center gap-3">
              <TreePine className={`w-5 h-5 ${showComforts ? 'text-emerald-500' : 'text-zinc-500'}`} />
              <div className="text-left">
                <div className={`text-sm font-medium ${showComforts ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  Conforts
                  <span className="ml-2 text-xs opacity-70">({comfortCount})</span>
                </div>
                <div className="text-xs text-zinc-500">Aires de pique-nique + LOS</div>
              </div>
            </div>
            {showComforts ? (
              <Eye className="w-4 h-4 text-emerald-400" />
            ) : (
              <EyeOff className="w-4 h-4 text-zinc-500" />
            )}
          </button>
        </div>
      </div>

      {/* Cached Searches Info + Reset Button */}
      {cachedSearches.length > 0 && (
        <div className="px-5 py-3 border-b border-white/10 bg-blue-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-blue-400 text-xs">
              <div className="w-2 h-2 rounded-full bg-blue-400"></div>
              <span>{cachedSearches.length} recherche(s) en cache</span>
            </div>
            <button
              onClick={handleResetCache}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-red-500/10"
              data-testid="reset-cache-button"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Search Button */}
      <div className="px-5 py-4">
        <button
          onClick={onSearch}
          disabled={isCalculating}
          className="w-full h-12 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white font-semibold text-sm rounded-md transition-all duration-200 hover:from-emerald-500 hover:to-emerald-400 hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="start-search-button"
        >
          {isCalculating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyse en cours...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              Rechercher les Spots
            </>
          )}
        </button>
      </div>
    </div>
  );
};
